import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import Input from "../../../components/form-item/Input";
import Textarea from "../../../components/form-item/Textarea";
import ListBlock from "../../../components/block/ListBlock";
import IconButton from "../../../components/button/IconButton";
import Modal from "../../../components/modal/Modal";
import TitleBlock from "../../../components/block/TitleBlock";
import {
  SortableDragHandle,
  SortableItem,
  SortableList,
} from "../../../components/sortable";
import {
  useGetProfileContentQuery,
  useUpdateProfileContentMutation,
} from "../../../app/services/creatorProfile";
import type { ProfileTextBlock } from "../../../types/profile";

interface DraftBlock {
  key: string;
  section_name_jp: string;
  section_name_en: string;
  content_jp: string;
  content_en: string;
  is_link_menu: boolean;
}

const newKey = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `row-${Date.now()}-${Math.random()}`;

const toDraft = (b: ProfileTextBlock): DraftBlock => ({
  key: newKey(),
  section_name_jp: b.section_name_jp,
  section_name_en: b.section_name_en,
  content_jp: b.content_jp,
  content_en: b.content_en,
  is_link_menu: b.is_link_menu,
});

const emptyDraft = (): DraftBlock => ({
  key: newKey(),
  section_name_jp: "",
  section_name_en: "",
  content_jp: "",
  content_en: "",
  is_link_menu: false,
});

/**
 * プロフィールテキスト編集。
 *
 * 本文が長くリアルタイム保存に向かないため、セクションごとにブロックを並べ、
 * クリックで詳細モーダルを開いて編集 →「OK」で保存する方式。`/api/me/pages/
 * profile/content` の profile_texts を更新する（マークダウン対応）。
 */
const MySiteText: React.FC = () => {
  const { data: contentResp } = useGetProfileContentQuery();
  const [updateContent, { isLoading: isSaving }] = useUpdateProfileContentMutation();
  const [blocks, setBlocks] = useState<DraftBlock[]>([]);
  // 編集中の 1 ブロック（モーダル）。null のとき閉じている。
  const [editing, setEditing] = useState<DraftBlock | null>(null);
  const [isNew, setIsNew] = useState(false);

  useEffect(() => {
    if (contentResp?.data) {
      const sorted = [...contentResp.data.profile_texts].sort((a, b) => a.order - b.order);
      setBlocks(sorted.map(toDraft));
    }
  }, [contentResp]);

  /** 確定したブロック配列をサーバへ保存する（モーダルの「OK」/ 行の削除 / 並び替え）。
   *  配列インデックスを `order` として送るため、保存順がそのまま公開ページの表示順になる。 */
  const persist = async (nextBlocks: DraftBlock[]) => {
    const profile_texts: ProfileTextBlock[] = nextBlocks.map((b, i) => ({
      id: 0,
      section_name_jp: b.section_name_jp,
      section_name_en: b.section_name_en,
      content_jp: b.content_jp,
      content_en: b.content_en,
      is_link_menu: b.is_link_menu,
      order: i,
    }));
    try {
      await updateContent({ profile_texts }).unwrap();
      setBlocks(nextBlocks);
      toast.success("保存しました");
    } catch {
      toast.error("保存に失敗しました");
    }
  };

  const openAdd = () => {
    setEditing(emptyDraft());
    setIsNew(true);
  };
  const openEdit = (block: DraftBlock) => {
    setEditing({ ...block });
    setIsNew(false);
  };
  const closeModal = () => setEditing(null);

  const handleOk = async () => {
    if (!editing) return;
    const next = isNew
      ? [...blocks, editing]
      : blocks.map((b) => (b.key === editing.key ? editing : b));
    await persist(next);
    closeModal();
  };

  /** 一覧の削除アイコンから直接ブロックを削除する。 */
  const handleDeleteBlock = (key: string) => persist(blocks.filter((b) => b.key !== key));

  /**
   * ドラッグ並び替え確定。新しい key 順に `blocks` を組み直して保存する。
   * `persist` が配列 index を `order` に書き込むため、公開ページの表示順にも反映される。
   */
  const handleReorder = async (newIds: string[]) => {
    const map = new Map(blocks.map((b) => [b.key, b]));
    const next = newIds
      .map((id) => map.get(id))
      .filter((b): b is DraftBlock => !!b);
    await persist(next);
  };

  const patchEditing = (patch: Partial<DraftBlock>) =>
    setEditing((prev) => (prev ? { ...prev, ...patch } : prev));

  return (
    <div className="u-col u-gap16">
      <TitleBlock
        title="プロフィールテキスト"
        description="自分を伝える文章・実績・バイオグラフィ（Markdown対応）。各ブロックをクリックして編集します。"
      />

      <div className="u-w100 u-col u-gap8">
        <SortableList
          items={blocks}
          getId={(b) => b.key}
          strategy="vertical"
          axis="y"
          onReorder={handleReorder}
        >
          {blocks.map((block) => (
            <SortableItem id={block.key} key={block.key}>
              {({ setNodeRef, style, handleProps }) => (
                <div
                  ref={setNodeRef}
                  className="c-group u-row u-gap8 u-w100"
                  style={{ ...style, alignItems: "center" }}
                >
                  <SortableDragHandle {...handleProps} />
                  <div
                    className="u-col u-gap4"
                    style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                    onClick={() => openEdit(block)}
                  >
                    <span className="heading-13">
                      {block.section_name_jp || "（無題のブロック）"}
                    </span>
                    <span
                      className="bodytext-11 u-text-light-gray"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {block.content_jp || "（本文未入力）"}
                    </span>
                  </div>
                  <IconButton
                    icon="delete"
                    size={18}
                    onClick={() => handleDeleteBlock(block.key)}
                    ariaLabel="削除"
                    title="削除"
                  />
                </div>
              )}
            </SortableItem>
          ))}
        </SortableList>
        <ListBlock type="add" label="ブロックを追加" onClick={openAdd} />
      </div>

      {editing && (
        <Modal
          onClose={closeModal}
          title={isNew ? "プロフィールブロックを追加" : "プロフィールブロックを編集"}
          size="default"
          primaryAction={{ label: "OK", onClick: handleOk, loading: isSaving }}
          secondaryAction={{ label: "キャンセル", onClick: closeModal }}
        >
          <div className="u-col u-gap16 u-w100">
            <Input
              label="セクション名（日本語）"
              type="text"
              maxlength={15}
              value={editing.section_name_jp}
              onChange={(e) => patchEditing({ section_name_jp: e.target.value })}
              placeholder="プロフィール"
            />
            <Input
              label="セクション名（English）"
              type="text"
              maxlength={15}
              value={editing.section_name_en}
              onChange={(e) => patchEditing({ section_name_en: e.target.value })}
              placeholder="Profile"
            />
            <Textarea
              label="本文（日本語・Markdown）"
              rows={8}
              maxLength={3000}
              value={editing.content_jp}
              onChange={(e) => patchEditing({ content_jp: e.target.value })}
              placeholder="経歴や活動内容を記述します。**強調** や - 箇条書き などの Markdown が使えます。"
            />
            <Textarea
              label="本文（English・Markdown）"
              rows={8}
              maxLength={3000}
              value={editing.content_en}
              onChange={(e) => patchEditing({ content_en: e.target.value })}
            />
            <label className="u-row u-gap8" style={{ cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={editing.is_link_menu}
                onChange={(e) => patchEditing({ is_link_menu: e.target.checked })}
              />
              <span className="bodytext-13">トップメニューにリンクする</span>
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MySiteText;
